import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Table, Spin, Alert, Button, Tag, Card, Modal, Form, Input, Select, message, Popconfirm, Space, Row, Col, Avatar, Checkbox, AutoComplete, Dropdown, Typography } from 'antd'; // Added AutoComplete and Typography
import { PlusOutlined, EditOutlined, DeleteOutlined, AimOutlined, HolderOutlined, FileTextOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import PermissionGuard from './PermissionGuard';
import { useTranslation } from 'react-i18next';
import { getAllOrganSystems, getOrganSystemInfo, ORGAN_SYSTEM_COLORS } from '../utils/organSystemConstants';
import OrganSystemTags, { IconOnlyOrganSystemTags } from './OrganSystemTags';
import '../styles/ProblemList.css';
import '../styles/ProblemPlan.css';
import MomentDatePicker from './MomentDatePicker';
import ProblemPlanModal from './ProblemPlanModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Option } = Select;
const { TextArea } = Input;
const { Group: CheckboxGroup } = Checkbox;
const { Text } = Typography;

// Sortable Row Component for drag-and-drop
const SortableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === 'drag') {
          return React.cloneElement(child, {
            children: (
              <div
                {...listeners}
                style={{
                  touchAction: 'none',
                  cursor: 'grab',
                }}
              >
                <HolderOutlined />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

// Helper to format date
const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
};

const PatientProblemList = ({ patientId, onCreateSmartGoal }) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null); // null for Add, object for Edit
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [organSystems, setOrganSystems] = useState([]);
  const [problemNameOptions, setProblemNameOptions] = useState([]);
  const [smartGoalsCounts, setSmartGoalsCounts] = useState({}); // NEW: Track SMART Goals count per problem
  const { user } = useAuth(); // For recordedBy and role checks
  const { hasPermission } = usePermissions();
  const { t, i18n } = useTranslation();

  // Plan Modal states
  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
  const [selectedProblemForPlan, setSelectedProblemForPlan] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleProblemSearch = async (searchText) => {
    if (searchText.length < 3) {
      setProblemNameOptions([]);
      return;
    }
    try {
      const response = await axios.get(`/medical-terms/search`, {
        params: { term: searchText }
      });
      const options = response.data.data.map(term => ({
        value: term.description,
        key: term.snomedCID,
        // Pass the whole term object to be used in onProblemSelect if needed
        term: term,
      }));
      setProblemNameOptions(options);
    } catch (error) {
      console.error('Error searching for problems:', error);
      setProblemNameOptions([]);
    }
  };

  const fetchProblems = useCallback(async () => {
    console.log('PatientProblemList - Fetching problems for patientId:', patientId); // DEBUG LOG
    if (!patientId) {
        console.error('PatientProblemList - patientId is missing!'); // DEBUG LOG
        setError('Patient ID is missing.');
        setLoading(false);
        return;
    }
    setLoading(true);
    setError(null);
    try {
      // Fetch all problems (active and inactive/resolved) for the patient
      const response = await axios.get(`/patients/${patientId}/problems`); // Removed prefix
      setProblems(response.data.data.problems);
    } catch (err) {
      // Enhanced error logging
      console.error("Error fetching problems:", err);
      if (err.response) {
        // Request made and server responded
        console.error("Error Response Data:", err.response.data);
        console.error("Error Response Status:", err.response.status);
        console.error("Error Response Headers:", err.response.headers);
        setError(`Server Error ${err.response.status}: ${err.response.data?.message || 'Unknown server error'}`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error("Error Request:", err.request);
        setError('No response received from server. Check network or backend status.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error Message:', err.message);
        setError(`Request setup error: ${err.message}`);
      }
      // Keep the generic message as a final fallback if needed, but prioritize specific errors
      // setError(err.response?.data?.message || 'Failed to fetch problems.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // NEW: Fetch SMART Goals counts for problems
  const fetchSmartGoalsCounts = useCallback(async () => {
    try {
      const response = await axios.get(`/patients/${patientId}/smart-goals`);
      const smartGoals = response.data.data.smartGoals;

      // Count goals per problem
      const counts = {};
      problems.forEach(problem => {
        counts[problem._id] = smartGoals.filter(goal => goal.problem?._id === problem._id).length;
      });

      setSmartGoalsCounts(counts);
    } catch (err) {
      console.error('Error fetching SMART Goals counts:', err);
      // Don't show error for this, just set empty counts
      setSmartGoalsCounts({});
    }
  }, [patientId, problems]);

  // Fetch organ systems on component mount
  const fetchOrganSystems = useCallback(async () => {
    try {
      const response = await axios.get('/problems/organ-systems');
      setOrganSystems(response.data.data.organSystems);
    } catch (err) {
      console.error("Error fetching organ systems:", err);
      // Fallback to local constants if API fails
      setOrganSystems(getAllOrganSystems());
    }
  }, []);

  useEffect(() => {
    fetchProblems();
    fetchOrganSystems();
  }, [fetchProblems, fetchOrganSystems]);

  // NEW: Fetch SMART Goals counts when problems change
  useEffect(() => {
    if (problems.length > 0) {
      fetchSmartGoalsCounts();
    }
  }, [problems, fetchSmartGoalsCounts]);

  const showModal = (problem = null) => {
    setEditingProblem(problem);
    if (problem) {
      // Pre-fill form for editing
      form.setFieldsValue({
        ...problem,
        onsetDate: problem.onsetDate ? moment(problem.onsetDate) : null,
        resolvedDate: problem.resolvedDate ? moment(problem.resolvedDate) : null,
      });
    } else {
      // Reset form for adding
      form.resetFields();
      form.setFieldsValue({
        status: 'Active', // Default status for new problems
        priority: 'Medium' // Default priority for new problems
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingProblem(null);
    form.resetFields();
  };

  const handleFinish = async (values) => {
    if (!user || !user._id) {
        message.error('User not logged in.');
        return;
    }

    // Client-side validation
    if (!values.problemName || values.problemName.trim().length === 0) {
      message.error('Problem name is required.');
      return;
    }

    if (!values.organSystems || !Array.isArray(values.organSystems) || values.organSystems.length === 0) {
      message.error('Please select at least one organ system.');
      return;
    }

    if (!values.status) {
      message.error('Please select a status.');
      return;
    }

    if (!values.priority) {
      message.error('Please select a priority level.');
      return;
    }

    // Validate onset date is not in the future
    if (values.onsetDate && values.onsetDate.isAfter(moment())) {
      message.error('Onset date cannot be in the future.');
      return;
    }

    // Validate resolved date logic
    if (values.status === 'Resolved' && values.resolvedDate) {
      if (values.onsetDate && values.resolvedDate.isBefore(values.onsetDate)) {
        message.error('Resolved date cannot be before onset date.');
        return;
      }
      if (values.resolvedDate.isAfter(moment())) {
        message.error('Resolved date cannot be in the future.');
        return;
      }
    }

    setSubmitLoading(true);
    setError(null); // Clear modal-specific error

    const payload = {
      ...values,
      problemName: values.problemName.trim(),
      onsetDate: values.onsetDate ? values.onsetDate.toISOString() : null,
      resolvedDate: values.resolvedDate ? values.resolvedDate.toISOString() : null,
      // recordedBy and patient are handled by backend/route
    };

    try {
      if (editingProblem) {
        // Update existing problem
        await axios.patch(`/problems/${editingProblem._id}`, payload); // Removed prefix
        message.success('Problem updated successfully!');
      } else {
        // Create new problem
        await axios.post(`/patients/${patientId}/problems`, payload); // Removed prefix
        message.success('Problem added successfully!');
      }
      handleCancel();
      fetchProblems(); // Refresh list
    } catch (err) {
      console.error("Error saving problem:", err);

      // Enhanced error handling
      let errorMessage = 'Failed to save problem.';

      if (err.response) {
        const { status, data } = err.response;

        switch (status) {
          case 400:
            errorMessage = data.message || 'Invalid data provided. Please check your inputs.';
            break;
          case 401:
            errorMessage = 'You are not authorized to perform this action.';
            break;
          case 403:
            errorMessage = 'You do not have permission to manage problems.';
            break;
          case 404:
            errorMessage = 'Patient not found.';
            break;
          case 422:
            errorMessage = 'Validation error: ' + (data.message || 'Please check your inputs.');
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      message.error(errorMessage);
      setError(errorMessage); // Show error in modal
    } finally {
      setSubmitLoading(false);
    }
  };

   const handleDelete = async (problemId) => {
    try {
      await axios.delete(`/problems/${problemId}`); // Removed prefix
      message.success('Problem deleted successfully!');
      fetchProblems(); // Refresh list
    } catch (err) {
      console.error("Error deleting problem:", err);

      // Enhanced error handling for delete
      let errorMessage = 'Failed to delete problem.';

      if (err.response) {
        const { status, data } = err.response;

        switch (status) {
          case 401:
            errorMessage = 'You are not authorized to delete problems.';
            break;
          case 403:
            errorMessage = 'You do not have permission to delete this problem.';
            break;
          case 404:
            errorMessage = 'Problem not found or already deleted.';
            break;
          case 409:
            errorMessage = 'Cannot delete problem: it may be referenced by other records.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      message.error(errorMessage);
    }
  };

  // Handle drag end for reordering problems
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = problems.findIndex((item) => item._id === active.id);
      const newIndex = problems.findIndex((item) => item._id === over.id);

      const newProblems = arrayMove(problems, oldIndex, newIndex);

      // Update local state immediately for better UX
      setProblems(newProblems);

      try {
        // Create the sort order array
        const problemOrders = newProblems.map((problem, index) => ({
          problemId: problem._id,
          sortOrder: index
        }));

        // Send to backend
        await axios.put(`/patients/${patientId}/problems/sort-order`, {
          problemOrders
        });

        message.success('Problem order updated successfully!');
      } catch (err) {
        console.error('Error updating problem order:', err);
        message.error('Failed to update problem order');
        // Revert local state on error
        fetchProblems();
      }
    }
  };

  // Handle status update
  const handleStatusUpdate = async (problemId, newStatus) => {
    try {
      await axios.patch(`/problems/${problemId}`, {
        status: newStatus
      });

      // Update local state
      setProblems(prevProblems =>
        prevProblems.map(problem =>
          problem._id === problemId
            ? { ...problem, status: newStatus }
            : problem
        )
      );

      message.success('Problem status updated successfully!');
    } catch (err) {
      console.error('Error updating problem status:', err);
      message.error('Failed to update problem status');
    }
  };

  // Handle priority update
  const handlePriorityUpdate = async (problemId, newPriority) => {
    try {
      await axios.patch(`/problems/${problemId}`, {
        priority: newPriority
      });

      // Update local state
      setProblems(prevProblems =>
        prevProblems.map(problem =>
          problem._id === problemId
            ? { ...problem, priority: newPriority }
            : problem
        )
      );

      message.success('Problem priority updated successfully!');
    } catch (err) {
      console.error('Error updating problem priority:', err);
      message.error('Failed to update problem priority');
    }
  };

  // Handle plan modal
  const showPlanModal = (problem) => {
    setSelectedProblemForPlan(problem);
    setIsPlanModalVisible(true);
  };

  const handlePlanModalCancel = () => {
    setIsPlanModalVisible(false);
    setSelectedProblemForPlan(null);
  };

  const handlePlanUpdated = (updatedProblem) => {
    // Update the problem in the local state
    setProblems(prevProblems =>
      prevProblems.map(problem =>
        problem._id === updatedProblem._id ? updatedProblem : problem
      )
    );
  };

  const columns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} />,
    },
    {
      title: t('patientChart.problemList.problemName'),
      dataIndex: 'problemName',
      key: 'problemName',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Organ Systems',
      dataIndex: 'organSystems',
      key: 'organSystems',
      width: 250,
      responsive: ['md'],
      render: (organSystemKeys) => (
        <IconOnlyOrganSystemTags
          organSystems={organSystemKeys}
          showMore={false}
          size="small"
          maxRows={2}
          iconsPerRow={4}
        />
      ),
      filters: organSystems.map(system => ({
        text: system.name,
        value: system.key
      })),
      onFilter: (value, record) => record.organSystems && record.organSystems.includes(value),
    },
    {
      title: t('patientChart.problemList.icdCode'),
      dataIndex: 'icdCode',
      key: 'icdCode',
      width: 100,
      responsive: ['lg'],
      render: (text) => text || 'N/A',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority, record) => {
        const priorityOptions = [
          { key: 'Critical', label: 'Critical', color: 'red' },
          { key: 'High', label: 'High', color: 'orange' },
          { key: 'Medium', label: 'Medium', color: 'blue' },
          { key: 'Low', label: 'Low', color: 'green' }
        ];

        const items = priorityOptions.map(option => ({
          key: option.key,
          label: (
            <span onClick={() => handlePriorityUpdate(record._id, option.key)}>
              <Tag color={option.color} style={{ margin: 0 }}>
                {option.label}
              </Tag>
            </span>
          ),
        }));

        return (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Tag
              color={
                priority === 'Critical' ? 'red' :
                priority === 'High' ? 'orange' :
                priority === 'Medium' ? 'blue' :
                priority === 'Low' ? 'green' : 'default'
              }
              style={{ cursor: 'pointer' }}
            >
              {priority}
            </Tag>
          </Dropdown>
        );
      },
      filters: [
        { text: 'Critical', value: 'Critical' },
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
      ],
      onFilter: (value, record) => record.priority === value,
      sorter: (a, b) => {
        const priorityOrder = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      },
    },
    {
      title: t('patientChart.problemList.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const statusOptions = [
          { key: 'Active', label: 'Active', color: 'green' },
          { key: 'Resolved', label: 'Resolved', color: 'blue' },
          { key: 'Inactive', label: 'Inactive', color: 'orange' }
        ];

        const items = statusOptions.map(option => ({
          key: option.key,
          label: (
            <span onClick={() => handleStatusUpdate(record._id, option.key)}>
              <Tag color={option.color} style={{ margin: 0 }}>
                {option.label}
              </Tag>
            </span>
          ),
        }));

        return (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Tag
              color={status === 'Active' ? 'green' : status === 'Resolved' ? 'blue' : 'orange'}
              style={{ cursor: 'pointer' }}
            >
              {status}
            </Tag>
          </Dropdown>
        );
      },
      filters: [
        { text: t('patientChart.problemList.active'), value: 'Active' },
        { text: t('patientChart.problemList.resolved'), value: 'Resolved' },
        { text: t('patientChart.problemList.inactive'), value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status.includes(value),
    },
    {
      title: t('patientChart.problemList.onsetDate'),
      dataIndex: 'onsetDate',
      key: 'onsetDate',
      render: formatDate,
      sorter: (a, b) => new Date(a.onsetDate || 0) - new Date(b.onsetDate || 0),
    },
    {
      title: t('patientChart.problemList.resolvedDate'),
      dataIndex: 'resolvedDate',
      key: 'resolvedDate',
      render: formatDate,
    },
    {
      title: t('patientChart.problemList.notes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true, // Show ellipsis for long notes
      responsive: ['xl'],
      width: 200,
    },
    {
      title: 'SMART Goals',
      key: 'smartGoals',
      width: 100,
      align: 'center',
      render: (text, record) => {
        const count = smartGoalsCounts[record._id] || 0;
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ color: count > 0 ? '#1890ff' : '#999' }}>
              {count}
            </Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {count === 1 ? 'goal' : 'goals'}
            </Text>
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          <Space size="small" wrap>
            {canModify && (
              <Button icon={<EditOutlined />} onClick={() => showModal(record)} size="small">
                <span className="hidden-mobile">Edit</span>
              </Button>
            )}
            {canModify && (
              <Button
                icon={<FileTextOutlined />}
                onClick={() => showPlanModal(record)}
                size="small"
                type="default"
                className="plan-button"
              >
                <span className="hidden-mobile">Plan</span>
              </Button>
            )}
            {canModify && (
              <Button
                icon={<AimOutlined />}
                onClick={() => onCreateSmartGoal && onCreateSmartGoal(record)}
                size="small"
                type="primary"
                ghost
              >
                <span className="hidden-mobile">Goal</span>
              </Button>
            )}
            {/* Add delete confirmation */}
            {canModify && (
              <Popconfirm
                title="Are you sure you want to delete this problem?"
                onConfirm={() => handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
              >
                 <Button icon={<DeleteOutlined />} danger size="small">
                   <span className="hidden-mobile">Delete</span>
                 </Button>
              </Popconfirm>
            )}
          </Space>
        </Space>
      ),
    },
  ];

  // Check if user has permission to add/edit/delete using new permission system
  const canModify = hasPermission('clinical.problems.manage');

  return (
    <Card title={t('patientChart.problemList.title')}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      {canModify && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showModal(null)}
          style={{ marginBottom: 16 }}
        >
          {t('patientChart.problemList.addProblem')}
        </Button>
      )}
      <Spin spinning={loading}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={problems.map(p => p._id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              className="no-wrap-table-header" // Add this class name
              columns={columns}
              dataSource={problems}
              rowKey="_id"
              pagination={false} // Remove pagination to enable full list drag-and-drop reordering
              size="small"
              scroll={{ x: 800, y: 600 }} // Enable horizontal scroll on mobile and vertical scroll for long lists
              responsive={true}
              components={{
                body: {
                  row: SortableRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </Spin>

      {/* Add/Edit Problem Modal */}
      <Modal
        title={editingProblem ? t('patientChart.problemList.editProblem') : t('patientChart.problemList.addNewProblem')}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={submitLoading}>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
          <Form form={form} layout="vertical" onFinish={handleFinish} className="problem-form-mobile">
            <Form.Item
              name="problemName"
              label={t('patientChart.problemList.problemName')}
              rules={[
                { required: true, message: t('patientChart.problemList.problemNameRequired') },
                { min: 2, message: 'Problem name must be at least 2 characters long' },
                { max: 200, message: 'Problem name cannot exceed 200 characters' },
                { whitespace: true, message: 'Problem name cannot be empty or only whitespace' }
              ]}
            >
              <AutoComplete
                options={problemNameOptions}
                onSearch={handleProblemSearch}
                placeholder="Enter problem name"
              />
            </Form.Item>
            <Form.Item
              name="organSystems"
              label="Organ Systems"
              rules={[
                { required: true, message: 'Please select at least one organ system' },
                { type: 'array', min: 1, message: 'Please select at least one organ system' }
              ]}
            >
              <CheckboxGroup style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  {organSystems.map(system => (
                    <Col xs={12} sm={8} md={6} lg={4} key={system.key}>
                      <Checkbox
                        value={system.key}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '8px',
                          margin: '4px 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          minHeight: '80px',
                          justifyContent: 'center'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <Avatar
                            src={system.iconPath}
                            size={32}
                            style={{
                              backgroundColor: ORGAN_SYSTEM_COLORS[system.key],
                              width: '32px',
                              height: '32px',
                              minWidth: '32px'
                            }}
                          />
                          <div style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: '1.2' }}>
                            {system.name}
                          </div>
                        </div>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </CheckboxGroup>
            </Form.Item>
            <Form.Item
              name="icdCode"
              label={t('patientChart.problemList.icdCode')}
              rules={[
                { max: 20, message: 'ICD code cannot exceed 20 characters' },
                { pattern: /^[A-Z0-9.-]*$/, message: 'ICD code can only contain uppercase letters, numbers, dots, and hyphens' }
              ]}
            >
              <Input placeholder="e.g., M79.3" maxLength={20} style={{ textTransform: 'uppercase' }} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="status" label={t('patientChart.problemList.status')} rules={[{ required: true }]}>
                  <Select>
                    <Option value="Active">{t('patientChart.problemList.active')}</Option>
                    <Option value="Resolved">{t('patientChart.problemList.resolved')}</Option>
                    <Option value="Inactive">{t('patientChart.problemList.inactive')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                  <Select>
                    <Option value="Critical">Critical</Option>
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                 <Form.Item name="onsetDate" label={t('patientChart.problemList.onsetDate')}>
                   <MomentDatePicker style={{ width: '100%' }} />
                 </Form.Item>
              </Col>
            </Row>
             {/* Show Resolved Date only if status is Resolved */}
             {form.getFieldValue('status') === 'Resolved' && (
                <Form.Item
                  name="resolvedDate"
                  label={t('patientChart.problemList.resolvedDate')}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        const onsetDate = form.getFieldValue('onsetDate');
                        if (onsetDate && value.isBefore(onsetDate)) {
                          return Promise.reject(new Error('Resolved date cannot be before onset date'));
                        }

                        if (value.isAfter(moment())) {
                          return Promise.reject(new Error('Resolved date cannot be in the future'));
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <MomentDatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current && current.isAfter(moment().endOf('day'))}
                  />
                </Form.Item>
             )}
            <Form.Item
              name="notes"
              label={t('patientChart.problemList.notes')}
              rules={[
                { max: 1000, message: 'Notes cannot exceed 1000 characters' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Additional notes about the problem..."
                maxLength={1000}
                showCount
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingProblem ? t('patientChart.problemList.saveChanges') : t('patientChart.problemList.addProblem')}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Problem Plan Modal */}
      <ProblemPlanModal
        visible={isPlanModalVisible}
        onCancel={handlePlanModalCancel}
        problem={selectedProblemForPlan}
        onPlanUpdated={handlePlanUpdated}
      />
    </Card>
  );
};

export default PatientProblemList;
